import { useMemo } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import { useSelectedReleaseIdForRoute } from '../../../redux/project_grouping/hooks';
import { nuc } from '../../../utils';
import { isReleaseGroupScope } from '../types';
import type { ReportsV3Params } from '../types';

import { useExecutiveOptionsParams } from './useExecutiveOptionsParams';

/**
 * Builds the preview URL for the executive vulnerability report.
 * Same pattern as `useRemediationPreviewURL` but targets the executive-vulnerability endpoints.
 */
export const useExecutivePreviewURL = (): string => {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelectedReleaseIdForRoute();
  const [isFullList] = useExecutiveOptionsParams();

  return useMemo(() => {
    // The report is PDF only, but useLoadPreview reads `format` to skip its empty-page check on PDFs.
    const query = `preview=true&format=PDF&fullList=${isFullList}`;

    if (isReleaseGroup) {
      const { projectGroupId, releaseId } = params;
      const resolvedReleaseId = releaseId ?? selectedReleaseId ?? 0;
      return `/api/project_group/${projectGroupId}/release/${resolvedReleaseId}/report/executive-vulnerability?${query}`;
    }

    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api/revisions/${projectLocator}/report/executive-vulnerability?${query}`;
  }, [isFullList, isReleaseGroup, params, selectedReleaseId]);
};
